import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import BackgroundAnimation from '../components/BackgroundAnimation';
import {
  TrendingUp,
  MessageCircle,
  Settings,
  Target,
  DollarSign,
  Bot,
  Zap,
  Globe,
  Users,
  BarChart3,
  Shield,
  Headphones,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Rocket,
  Code,
  Megaphone
} from 'lucide-react';

const Spline = lazy(() => import('@splinetool/react-spline'));

// Check current theme
const useTheme = () => {
  // Initialize based on actual DOM state if available
  const getInitialTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') || !document.documentElement.classList.contains('light');
    }
    return true;
  };
  
  const [isDark, setIsDark] = React.useState(getInitialTheme);
  
  React.useEffect(() => {
    const checkTheme = () => {
      const isDarkMode = document.documentElement.classList.contains('dark') || !document.documentElement.classList.contains('light');
      setIsDark(isDarkMode);
    };
    
    // Check immediately on mount
    checkTheme();
    
    // Observe changes to class
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);
  
  return isDark;
};

const Home = () => {
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { ref: servicesRef, inView: servicesInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { ref: testimonialsRef, inView: testimonialsInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const isDark = useTheme();

  const services = [
    {
      icon: Globe,
      title: 'Digital Web Solutions',
      description: 'Custom-built, lightning-fast websites engineered to captivate visitors and drive measurable conversions.',
      emoji: '🌐'
    },
    {
      icon: Bot,
      title: 'Intelligent Process Automation',
      description: 'AI-powered workflows that eliminate repetitive tasks and supercharge your team\'s productivity.',
      emoji: '🤖'
    },
    {
      icon: Megaphone,
      title: 'Social Presence Amplification',
      description: 'Strategic campaigns that grow your following, boost engagement, and build brand loyalty.',
      emoji: '📣'
    },
    {
      icon: Target,
      title: 'Revenue-Focused Lead Gen',
      description: 'Laser-targeted advertising that attracts high-intent prospects and maximizes your ad spend.',
      emoji: '🎯'
    },
    {
      icon: Sparkles,
      title: 'Brand Identity Evolution',
      description: 'Distinctive visual systems that tell your story and create lasting impressions in the marketplace.',
      emoji: '✨'
    }
  ];

  const testimonials = [
    {
      name: 'David Mitchell',
      role: 'CEO, CloudSync Technologies',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc2NjMwMTE0NXww&ixlib=rb-4.1.0&q=85&w=200',
      content: 'American Prime revolutionized how we operate. Their automation solutions freed up 30+ hours weekly, and we saw our conversions skyrocket within 90 days.'
    },
    {
      name: 'Jennifer Park',
      role: 'Marketing Director, Innovate Labs',
      image: 'https://images.unsplash.com/photo-1627161683077-e34782c24d81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc2NjMwMTE0NXww&ixlib=rb-4.1.0&q=85&w=200',
      content: 'They exceeded every expectation. Lead generation jumped 250%, and our sales pipeline became incredibly efficient with their CRM expertise.'
    },
    {
      name: 'Robert Anderson',
      role: 'Founder, Quantum Ventures',
      image: 'https://images.unsplash.com/photo-1642257859842-c95f9fa8121d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhlYWRzaG90fGVufDB8fHx8MTc2NjMwMTE0NXww&ixlib=rb-4.1.0&q=85&w=200',
      content: 'Partnering with American Prime was transformational. Their meticulous strategy delivered 5x returns in just four months. Absolutely remarkable.'
    }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', position: 'relative' }}>
      <BackgroundAnimation />
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center"
        style={{ padding: '120px 7.6923% 80px' }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full max-w-7xl mx-auto">
          {/* Left: Content */}
          <div className={`space-y-8 ${heroInView ? 'fade-in' : 'opacity-0'}`}>
            {/* Badges */}
            <div className="flex flex-wrap gap-4">
              <div
                className="px-4 py-2 rounded-full backdrop-blur-md"
                style={{
                  background: 'rgba(0, 255, 209, 0.1)',
                  border: '1px solid rgba(0, 255, 209, 0.3)'
                }}
              >
                <span className="body-small" style={{ color: 'var(--brand-primary)' }}>
                  AI-Powered
                </span>
              </div>
              <div
                className="px-4 py-2 rounded-full backdrop-blur-md"
                style={{
                  background: 'rgba(0, 255, 209, 0.1)',
                  border: '1px solid rgba(0, 255, 209, 0.3)'
                }}
              >
                <span className="body-small" style={{ color: 'var(--brand-primary)' }}>
                  High Performance
                </span>
              </div>
              <div
                className="px-4 py-2 rounded-full backdrop-blur-md"
                style={{
                  background: 'rgba(0, 255, 209, 0.1)',
                  border: '1px solid rgba(0, 255, 209, 0.3)'
                }}
              >
                <span className="body-small" style={{ color: 'var(--brand-primary)' }}>
                  Results Driven
                </span>
              </div>
            </div>

            <h1
              className="display-huge"
              style={{
                color: 'var(--text-primary)',
                lineHeight: '1.1'
              }}
            >
              Empowering Enterprises to
              <span className="block" style={{ color: 'var(--brand-primary)' }}>
                Grow Without Barriers
              </span>
              <span className="block">Through Smart Automation</span>
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px' }}>
              We blend innovation, artificial intelligence, and proven strategies to accelerate your business trajectory.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://calendly.com/americanprimeforyou"
                target="_blank"
                rel="noopener noreferrer"
                className="button-text dark-button-animate inline-flex items-center justify-center space-x-2"
                style={{
                  background: 'var(--brand-primary)',
                  color: '#000000',
                  padding: '14px 28px',
                  borderRadius: '0px',
                  border: 'none',
                  textDecoration: 'none'
                }}
              >
                <span>Book a Free Strategy Call</span>
                <ArrowRight size={20} />
              </a>
              <Link
                to="/services"
                className="button-text dark-button-animate inline-flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  padding: '14px 28px',
                  borderRadius: '0px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  textDecoration: 'none'
                }}
              >
                Explore Services
              </Link>
            </div>
          </div>

          {/* Right: Spline 3D Animation with Performance Fallback */}
          <div className="relative h-[600px] lg:h-[700px] overflow-visible">
            <Suspense
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  {/* Animated gradient fallback for better UX while loading */}
                  <div 
                    className="w-64 h-64 rounded-full animate-pulse"
                    style={{ 
                      background: 'radial-gradient(circle, var(--brand-primary) 0%, transparent 70%)',
                      opacity: 0.6
                    }}
                  />
                </div>
              }
            >
              <div 
                style={{ 
                  width: '700px', 
                  height: '700px', 
                  overflow: 'visible', 
                  position: 'relative',
                  filter: 'sepia(100%) hue-rotate(110deg) saturate(5) brightness(1.1)',
                  transition: 'filter 0.3s ease'
                }}
              >
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </div>
            </Suspense>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section
        ref={servicesRef}
        className="py-24"
        style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4">
            <div className="body-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
              Our Expertise
            </div>
            <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
              Solutions Engineered for Growth
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              End-to-end capabilities designed to propel your enterprise forward
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className={`p-8 rounded-lg dark-hover card-lift group ${servicesInView ? 'fade-in' : 'opacity-0'}`}
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid var(--border-subtle)',
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-14 h-14 rounded-lg flex items-center justify-center dark-transition"
                      style={{ background: 'rgba(0, 255, 209, 0.1)' }}
                    >
                      <Icon size={28} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <span className="text-3xl">{service.emoji}</span>
                  </div>
                  <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h3>
                  <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  <a
                    href="https://calendly.com/americanprimeforyou"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 body-medium dark-transition hover:opacity-80"
                    style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}
                  >
                    <span>Get Your Quote</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 255, 209, 0.02) 100%)',
          borderTop: '1px solid rgba(0, 255, 209, 0.1)',
          borderBottom: '1px solid rgba(0, 255, 209, 0.1)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center" style={{ padding: '0 7.6923%' }}>
          <h2 className="display-medium mb-6" style={{ color: 'var(--text-primary)' }}>
            Ready to Scale?
          </h2>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="button-text dark-button-animate inline-flex items-center space-x-2"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '14px 32px',
              borderRadius: '0px',
              textDecoration: 'none'
            }}
          >
            <span>Book a Call</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        ref={testimonialsRef}
        className="py-24"
        style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4">
            <div className="body-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
              Proven Impact
            </div>
            <h2 className="display-large mb-16" style={{ color: 'var(--text-primary)' }}>
              Outcomes That Speak Volumes
            </h2>
            <p className="body-large mb-16" style={{ color: 'var(--text-secondary)' }}>
              Discover how we've propelled businesses past their ambitious targets
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`p-8 rounded-lg dark-hover ${testimonialsInView ? 'fade-in' : 'opacity-0'}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  "{testimonial.content}"
                </p>
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="heading-3" style={{ color: 'var(--text-primary)' }}>
                      {testimonial.name}
                    </div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;